import {
  type RemixiconComponentType,
  RiAddLine,
  RiAlertLine,
  RiDeleteBinLine,
  RiFileListLine,
  RiNotification3Line,
  RiPencilLine,
  RiStarLine,
  RiTelegram2Line,
  RiVolumeUpLine,
} from "@remixicon/react";
import type { AlertRuleView, AlertTrigger } from "@screener/protocol";
import { Alert, AlertDescription, AlertTitle } from "@screener/uikit/components/alert";
import { Button } from "@screener/uikit/components/button";
import { LoadingRegion } from "@screener/uikit/components/loading-skeleton";
import { SectionLabel } from "@screener/uikit/components/section-label";
import { Skeleton } from "@screener/uikit/components/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import { type ReactNode, useEffect, useRef } from "react";
import { ExchangeLogo } from "@/density/exchange-logo";
import { SideBadge } from "@/density/side-badge";
import {
  type AlertActionKey,
  actionLabel,
  exchangeLabel,
  marketLabel,
  pluralForm,
} from "@/lib/labels";
import { ruleFilterLabel, TRIGGERS, volumePart, volumeRange } from "./rule-labels";

/**
 * `TRIGGERS.find(...)` типизируется как возможно `undefined`
 * (`noUncheckedIndexedAccess`), хотя на деле покрывает все восемь значений
 * `AlertTrigger` — единственное место в двух файлах экрана, где это нужно,
 * дублировать в `rule-labels.ts` не стали (design.md «Уже готово…»,
 * не переписывать committed-файлы).
 */
export function triggerInfo(trigger: AlertTrigger): { label: string; hint: string } {
  const found = TRIGGERS.find((entry) => entry.value === trigger);
  if (found) {
    return found;
  }
  console.error("неизвестный триггер алерта — TRIGGERS не покрывает значение", trigger);
  return { label: trigger, hint: "" };
}

/**
 * Список правил экрана «Алерты» (spec `web-alerts-screen`, 1.103, §116 п. 1–2;
 * design.md Д1–Д2): тулбар, таблица на всю ширину и строка-пример при пустом
 * списке. Презентационные компоненты без сети: состояние и запись живут в
 * `use-alert-rules.ts` и `alerts-screen.tsx`.
 */

export function ruleCountLabel(count: number): string {
  return `${count} ${pluralForm(count, "правило", "правила", "правил")}`;
}

/** Правила, у которых включено действие Telegram — для баннера и его счётчика. */
export function countRulesUsingTelegram(rules: readonly AlertRuleView[]): number {
  return rules.filter((view) => view.rule.telegram).length;
}

interface TelegramWarningBannerProps {
  /** Число правил с включённым Telegram — баннер показывается, только когда бот не задан. */
  count: number;
}

/**
 * Предупреждение о правилах с Telegram без бота (spec «Экран предупреждает
 * о правилах с Telegram без бота»). Показывается вызывающей стороной только
 * при `telegramConfigured === false && count > 0` — сам баннер этого не
 * проверяет, чтобы не дублировать условие в тесте и в разметке.
 */
export function TelegramWarningBanner({ count }: TelegramWarningBannerProps) {
  const sendersText = `${count} ${pluralForm(count, "правило шлёт", "правила шлют", "правил шлют")}`;
  return (
    <Alert variant="warning">
      <RiAlertLine aria-hidden="true" />
      <div className="flex flex-col gap-0.5">
        <AlertTitle className="font-normal">
          Бот Telegram не задан, а {sendersText} в Telegram
        </AlertTitle>
        <AlertDescription>
          Такие правила выполняют остальные свои действия, Telegram пропускается. Задайте токен и
          chat id в меню пользователя, пункт «Telegram-уведомления».
        </AlertDescription>
      </div>
    </Alert>
  );
}

/**
 * Колонки таблицы макета: событие, монета, биржа с рынком, сторона, размер,
 * действия, правка и удаление. Сетка включается по ширине контейнера экрана
 * (`@5xl/rules`), а не окна: рядом бывает боковая панель. Уже контейнера
 * строка — блок в два ряда, заголовки колонок скрыты (§116 п. 8).
 */
const WIDE_GRID =
  "@5xl/rules:grid @5xl/rules:grid-cols-[minmax(0,1.4fr)_150px_190px_80px_150px_132px_64px] @5xl/rules:flex-nowrap @5xl/rules:gap-y-0";
const ROW_BASE =
  "flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border pr-3 pl-4 text-[13px] tabular-nums";
const ROW_CLASS = cn(ROW_BASE, "py-2 @5xl/rules:h-8 @5xl/rules:py-0", WIDE_GRID);
const CELL = "flex min-w-0 items-center gap-1.5";
const MUTED = "text-muted-foreground";

const ACTIONS: readonly {
  key: AlertActionKey;
  label: string;
  Icon: RemixiconComponentType;
}[] = [
  { key: "sound", label: actionLabel("sound"), Icon: RiVolumeUpLine },
  { key: "notify", label: actionLabel("notify"), Icon: RiNotification3Line },
  { key: "telegram", label: actionLabel("telegram"), Icon: RiTelegram2Line },
  { key: "log", label: actionLabel("log"), Icon: RiFileListLine },
];

/** Размер кнопок и знаков действий, правки и удаления (макет: 26×26, radius 6). */
const ICON_BUTTON = "relative size-[26px] rounded-md [&_svg:not([class*='size-'])]:size-4";

/** Слово для пустого поля фильтра: «любая», «обе», «любой» — приглушённо, не пустой ячейкой (§6.5 1.20). */
function Unset({ children }: { children: string }) {
  return <span className={MUTED}>{children}</span>;
}

interface RuleCellsProps {
  spec: AlertRuleView["rule"];
  /** Ячейка действий — кнопки у настоящей строки, знаки у примера. */
  actions: ReactNode;
  /** Ячейка правки и удаления — кнопки у настоящей строки, подпись у примера. */
  tools: ReactNode;
}

/**
 * Семь ячеек строки правила — общие у настоящей строки и строки-примера, чтобы
 * пример выглядел ровно как правило. Разрыв `basis-full` делит строку блока
 * узкого окна на два ряда.
 */
function RuleCells({ spec, actions, tools }: RuleCellsProps) {
  const trigger = triggerInfo(spec.trigger);
  const volume = volumePart(spec);
  const range = volumeRange(spec);
  const mmLabel = spec.trigger === "situation_stacked" ? "без ММ-завалов" : "без ММ";
  return (
    <>
      <div className={cn(CELL, "min-w-36 flex-1 gap-2 @5xl/rules:min-w-0")}>
        <span className="sr-only">событие: </span>
        <span className="shrink-0 font-medium">{trigger.label}</span>
        <Tooltip>
          <TooltipTrigger render={<span className={cn("min-w-0 truncate text-xs", MUTED)} />}>
            {trigger.hint}
          </TooltipTrigger>
          <TooltipContent>{trigger.hint}</TooltipContent>
        </Tooltip>
      </div>
      <div data-slot="rule-cell-base" className={cn(CELL, "overflow-hidden")}>
        <span className="sr-only">монета: </span>
        {spec.base !== null ? (
          <span className="truncate font-semibold">{spec.base}</span>
        ) : (
          <Unset>любая</Unset>
        )}
        {spec.favorite_only ? (
          <Tooltip>
            <TooltipTrigger render={<span className="flex shrink-0" />}>
              <RiStarLine
                role="img"
                aria-label="только избранные"
                className={cn("size-3", MUTED)}
              />
            </TooltipTrigger>
            <TooltipContent>только избранные</TooltipContent>
          </Tooltip>
        ) : null}
        {spec.exclude_mm ? (
          <Tooltip>
            <TooltipTrigger render={<span className={cn("shrink-0 text-[11px]", MUTED)} />}>
              без ММ
              <span className="sr-only">{mmLabel === "без ММ" ? "" : "-завалов"}</span>
            </TooltipTrigger>
            <TooltipContent>{mmLabel}</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      <div data-slot="rule-cell-exchange" className={cn(CELL, "overflow-hidden")}>
        <span className="sr-only">биржа: </span>
        {spec.exchange !== null ? (
          <>
            <ExchangeLogo exchange={spec.exchange} decorative className="h-3" />
            <span className="truncate">{exchangeLabel(spec.exchange)}</span>
          </>
        ) : (
          <Unset>любая</Unset>
        )}
        {spec.market !== null ? (
          <span
            className={cn("shrink-0 text-[10px] font-medium tracking-[.08em] uppercase", MUTED)}
          >
            {marketLabel(spec.market)}
          </span>
        ) : (
          <span className={cn("shrink-0 text-[11px]", MUTED)}>любой рынок</span>
        )}
      </div>
      <div className={CELL}>
        <span className="sr-only">сторона: </span>
        {spec.side !== null ? <SideBadge side={spec.side} flagged={false} /> : <Unset>обе</Unset>}
      </div>
      <div className={cn(CELL, "@5xl/rules:justify-end")}>
        <span className="sr-only">размер: </span>
        {volume !== null && range !== null ? (
          <Tooltip>
            <TooltipTrigger render={<span className="truncate font-medium" />}>
              <span aria-hidden="true">{range}</span>
              <span className="sr-only">{volume}</span>
            </TooltipTrigger>
            <TooltipContent>{volume}</TooltipContent>
          </Tooltip>
        ) : (
          <Unset>любой</Unset>
        )}
      </div>
      <div className={cn(CELL, "gap-0.5")}>
        <span className="sr-only">действия: </span>
        {actions}
      </div>
      <div className={cn(CELL, "ml-auto gap-0.5 @5xl/rules:ml-0")}>{tools}</div>
    </>
  );
}

interface ActionToggleProps {
  action: (typeof ACTIONS)[number];
  enabled: boolean;
  /** Это единственное включённое действие правила — выключить его нельзя (§6.5 1.20). */
  isLast: boolean;
  /** Telegram включён, а бот не задан — сообщение не уйдёт (stage6-acceptance-debts). */
  warn: boolean;
  /** Где-то на экране идёт запись. */
  writing: boolean;
  onToggle: () => void;
}

/**
 * Одно из четырёх действий строки — кнопка, переключающая его (spec «Действие
 * правила переключается из его строки»). Состояние несёт `aria-pressed`, а не
 * только цвет: включённое стоит на плашке `bg-secondary`, выключенное — без
 * неё и тусклое. Подпись называет действие и то, что сделает нажатие.
 */
function ActionToggle({ action, enabled, isLast, warn, writing, onToggle }: ActionToggleProps) {
  const verb = enabled ? "Выключить" : "Включить";
  const label = isLast
    ? `${action.label}: нужно хотя бы одно действие`
    : warn
      ? `${verb}: ${action.label} — бот не задан, сообщение не уйдёт`
      : `${verb}: ${action.label}`;
  const button = (
    <Button
      variant="toggle"
      aria-pressed={enabled}
      aria-label={label}
      aria-disabled={isLast ? true : undefined}
      disabled={writing}
      onClick={isLast ? undefined : onToggle}
      className={cn(
        ICON_BUTTON,
        "p-0",
        warn && "text-warning",
        isLast && "aria-disabled:cursor-not-allowed",
      )}
    >
      <action.Icon aria-hidden="true" />
      {warn ? (
        <RiAlertLine
          aria-hidden="true"
          className="absolute -top-px -right-px size-2.5 text-warning"
        />
      ) : null}
    </Button>
  );
  return (
    <Tooltip>
      <TooltipTrigger render={button} />
      <TooltipContent>{isLast ? "нужно хотя бы одно действие" : label}</TooltipContent>
    </Tooltip>
  );
}

interface RuleRowProps {
  rule: AlertRuleView;
  telegramConfigured: boolean | null;
  /**
   * Где-то на экране уже идёт запись (ревью, пункт A.2) — недоступны кнопки
   * действий, «Изменить правило» (ревью, второй круг, пункт 4) и «Удалить
   * правило»: открытая форма или переключение во время другой записи потеряли
   * бы её исход.
   */
  writing: boolean;
  deleteButtonRef?: (el: HTMLButtonElement | null) => void;
  onEdit: () => void;
  onAskDelete: () => void;
  onToggleAction: (action: AlertActionKey) => void;
}

function RuleRow({
  rule,
  telegramConfigured,
  writing,
  deleteButtonRef,
  onEdit,
  onAskDelete,
  onToggleAction,
}: RuleRowProps) {
  const enabledCount = ACTIONS.filter((action) => rule.rule[action.key]).length;
  return (
    <li data-slot="rule-row" className={cn(ROW_CLASS, "hover:bg-foreground/5")}>
      <RuleCells
        spec={rule.rule}
        actions={ACTIONS.map((action) => (
          <ActionToggle
            key={action.key}
            action={action}
            enabled={rule.rule[action.key]}
            isLast={rule.rule[action.key] && enabledCount === 1}
            warn={action.key === "telegram" && rule.rule.telegram && telegramConfigured === false}
            writing={writing}
            onToggle={() => onToggleAction(action.key)}
          />
        ))}
        tools={
          <>
            <Button
              variant="bare"
              aria-label="Изменить правило"
              disabled={writing}
              onClick={onEdit}
              className={cn(ICON_BUTTON, MUTED, "hover:bg-accent hover:text-foreground")}
            >
              <RiPencilLine aria-hidden="true" />
            </Button>
            <Button
              ref={deleteButtonRef}
              variant="bare"
              aria-label="Удалить правило"
              disabled={writing}
              onClick={onAskDelete}
              className={cn(ICON_BUTTON, MUTED, "hover:bg-destructive/10 hover:text-destructive")}
            >
              <RiDeleteBinLine aria-hidden="true" />
            </Button>
          </>
        }
      />
    </li>
  );
}

/** Правило §8 из примера: Confirmed · LTC · bybit PERP · обе · от $2 000 000 · звук и Telegram. */
const EXAMPLE_RULE: AlertRuleView["rule"] = {
  exchange: "bybit",
  market: "perp",
  base: "LTC",
  side: null,
  min_usd: "2000000",
  max_usd: null,
  favorite_only: false,
  exclude_mm: false,
  trigger: "confirmed",
  sound: true,
  notify: false,
  telegram: true,
  log: false,
};

/**
 * Строка-пример пустого экрана (§116 п. 1): бледная, подписанная «пример»,
 * действия — знаки, а не кнопки, и не переключаются.
 */
function ExampleRow() {
  return (
    <li data-slot="rule-example" className={cn(ROW_CLASS, "opacity-45")}>
      <RuleCells
        spec={EXAMPLE_RULE}
        actions={ACTIONS.map((action) => (
          <Button
            key={action.key}
            variant="toggle"
            aria-hidden="true"
            aria-pressed={EXAMPLE_RULE[action.key]}
            tabIndex={-1}
            disabled
            className={cn(ICON_BUTTON, "p-0")}
          >
            <action.Icon aria-hidden="true" />
          </Button>
        ))}
        tools={<span className={cn("text-[11px] uppercase", MUTED)}>пример</span>}
      />
    </li>
  );
}

/** Заголовки колонок: только на широком контейнере, на узком их роль играют сами ячейки. */
function TableHeadRow() {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "hidden h-8 items-center gap-x-3 border-b border-border pr-3 pl-4",
        "@5xl/rules:grid @5xl/rules:grid-cols-[minmax(0,1.4fr)_150px_190px_80px_150px_132px_64px]",
      )}
    >
      <SectionLabel>Событие</SectionLabel>
      <SectionLabel>Монета</SectionLabel>
      <SectionLabel>Биржа · рынок</SectionLabel>
      <SectionLabel>Сторона</SectionLabel>
      <SectionLabel className="text-right">Размер</SectionLabel>
      <SectionLabel>Действия</SectionLabel>
      <SectionLabel>
        <span className="sr-only">Правка и удаление</span>
      </SectionLabel>
    </div>
  );
}

interface RuleDeleteConfirmProps {
  rule: AlertRuleView;
  /** Собственное удаление этой строки в пути — «Удалить» показывает «Удаляем…» (ревью, пункт A.3). */
  deleting: boolean;
  /** Где-то на экране уже идёт запись — «Удалить» недоступна (ревью, пункт A.2). */
  writing: boolean;
  /** Отказ последнего удаления — строка остаётся открытой и показывает текст (ревью, пункт A.3). */
  errorText: string | null;
  keepButtonRef?: (el: HTMLButtonElement | null) => void;
  /** «Удалить» этой строки — на неё возвращается фокус при отказе (ревью, второй круг, пункт 3). */
  confirmButtonRef?: (el: HTMLButtonElement | null) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

function RuleDeleteConfirm({
  rule,
  deleting,
  writing,
  errorText,
  keepButtonRef,
  confirmButtonRef,
  onCancel,
  onConfirm,
}: RuleDeleteConfirmProps) {
  const trigger = triggerInfo(rule.rule.trigger);
  const filterLabel = ruleFilterLabel(rule.rule);
  const containerRef = useRef<HTMLDivElement | null>(null);

  function handleConfirm() {
    // Фокус на контейнер строки — до того, как «Удалить» станет недоступна
    // (`disabled={writing}` следующим рендером): иначе браузер снимает фокус
    // с отключаемой кнопки в `document.body`, и место в списке теряется
    // (ревью, второй круг, пункт 3).
    containerRef.current?.focus();
    onConfirm();
  }

  return (
    <div
      role="alert"
      tabIndex={-1}
      ref={containerRef}
      className="flex flex-col gap-2 border-b border-border py-2 pr-3 pl-4 outline-none"
    >
      <div className="flex items-center gap-3">
        <RiAlertLine className="size-4 shrink-0 text-destructive" aria-hidden="true" />
        <div className="min-w-0 flex-1 text-sm">
          <span>
            Удалить правило {trigger.label} · {filterLabel}?
          </span>{" "}
          <span className="text-muted-foreground">Отменить удаление будет нельзя.</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* «Оставить» недоступна, пока идёт собственное удаление этой строки —
              запрос уже нельзя отменить, и уход отсюда потерял бы его исход. */}
          <Button
            ref={keepButtonRef}
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={deleting}
          >
            Оставить
          </Button>
          <Button
            ref={confirmButtonRef}
            variant="destructive"
            size="sm"
            onClick={handleConfirm}
            disabled={writing}
          >
            {deleting ? "Удаляем…" : "Удалить"}
          </Button>
        </div>
      </div>
      {errorText ? <p className="pl-7 text-xs text-destructive">{errorText}</p> : null}
    </div>
  );
}

function deleteErrorText(error: "unavailable" | "invalid"): string {
  return error === "unavailable"
    ? "Демон недоступен — правило не удалено."
    : "Демон не принял удаление.";
}

export interface RuleListProps {
  status: "loading" | "unavailable" | "ready";
  rules: readonly AlertRuleView[];
  /** `null` — статус бота ещё не пришёл; предупреждающий вид строк не показывается. */
  telegramConfigured: boolean | null;
  confirmingId: string | null;
  /** Где-то на экране уже идёт запись (ревью, пункт A.2) — «Удалить правило» и «Удалить» недоступны. */
  writing: boolean;
  /** Собственное удаление строки `confirmingId` в пути (ревью, пункт A.3). */
  deleting: boolean;
  /** Отказ последнего удаления — показывается в строке подтверждения, которая остаётся открытой. */
  deleteError: "unavailable" | "invalid" | null;
  /** Сообщение над списком — например, «правило удалено в другой вкладке» (ревью, пункт A.3). */
  notice: string | null;
  onNewRule: () => void;
  onEdit: (rule: AlertRuleView) => void;
  onAskDelete: (id: string) => void;
  onCancelDelete: () => void;
  onConfirmDelete: (id: string) => void;
  /** Переключение действия из строки (§116 п. 2); вызывающая сторона пишет правило целиком. */
  onToggleAction: (rule: AlertRuleView, action: AlertActionKey) => void;
  onRetry: () => void;
  /** Предупреждение между тулбаром и таблицей (Telegram без бота). */
  banner?: ReactNode;
}

/**
 * Секция «Правила» (spec «Экран показывает правила пользователя», «Удаление
 * подтверждается в строке»): заголовок со счётчиком и кнопкой заведения,
 * список, пустой список и «демон недоступен» — три различных состояния тела.
 */
export function RuleList({
  status,
  rules,
  telegramConfigured,
  confirmingId,
  writing,
  deleting,
  deleteError,
  notice,
  onNewRule,
  onEdit,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
  onToggleAction,
  onRetry,
  banner,
}: RuleListProps) {
  const newRuleButtonRef = useRef<HTMLButtonElement | null>(null);
  const keepButtonRef = useRef<HTMLButtonElement | null>(null);
  // «Удалить» строки подтверждения — принимает фокус обратно, когда своё
  // удаление отказало (ревью, второй круг, пункт 3): кнопка снова доступна
  // (`writing` спало), и место в списке не потеряно.
  const confirmDeleteButtonRef = useRef<HTMLButtonElement | null>(null);
  const deleteButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  // Порядок id на момент входа в подтверждение — после удаления строка уже
  // не в `rules`, и «следующую»/«предыдущую» нужно искать по этому снимку
  // (ревью, пункт G.11).
  const orderAtConfirmRef = useRef<string[]>([]);
  const lastConfirmingIdRef = useRef<string | null>(null);
  const lastDeleteErrorRef = useRef<"unavailable" | "invalid" | null>(null);

  function registerDeleteButtonRef(id: string) {
    return (el: HTMLButtonElement | null) => {
      if (el) {
        deleteButtonRefs.current.set(id, el);
      } else {
        deleteButtonRefs.current.delete(id);
      }
    };
  }

  useEffect(() => {
    const previous = lastConfirmingIdRef.current;
    if (confirmingId !== null && confirmingId !== previous) {
      // Вход в подтверждение — фокус на «Оставить» (ревью, пункт G.11).
      orderAtConfirmRef.current = rules.map((rule) => rule.id);
      keepButtonRef.current?.focus();
    } else if (confirmingId === null && previous !== null) {
      // Выход из подтверждения: кнопка удаления строки ещё существует —
      // это «Оставить» (строка осталась), иначе — успешное удаление, и
      // фокус переходит на соседнюю строку либо на «Новое правило».
      const stillThere = deleteButtonRefs.current.get(previous);
      if (stillThere) {
        stillThere.focus();
      } else {
        const order = orderAtConfirmRef.current;
        const index = order.indexOf(previous);
        const nextId = index >= 0 ? order[index + 1] : undefined;
        const prevId = index >= 0 ? order[index - 1] : undefined;
        const nextButton = nextId ? deleteButtonRefs.current.get(nextId) : undefined;
        const prevButton = prevId ? deleteButtonRefs.current.get(prevId) : undefined;
        const target = nextButton ?? prevButton ?? newRuleButtonRef.current;
        target?.focus();
      }
    }
    lastConfirmingIdRef.current = confirmingId;
  }, [confirmingId, rules]);

  useEffect(() => {
    // Отказ удаления («Демон недоступен…»/«Демон не принял…») возвращает
    // «Удалить» доступной — фокус идёт на неё, не теряется на контейнере,
    // куда его увёл `handleConfirm` перед тем, как кнопка стала недоступна
    // (ревью, второй круг, пункт 3).
    if (
      confirmingId !== null &&
      deleteError !== null &&
      deleteError !== lastDeleteErrorRef.current
    ) {
      confirmDeleteButtonRef.current?.focus();
    }
    lastDeleteErrorRef.current = deleteError;
  }, [confirmingId, deleteError]);

  return (
    <div className="@container/rules flex min-h-0 flex-1 flex-col">
      <div className="flex h-[38px] shrink-0 items-center gap-1.5 border-b border-border px-3">
        <Button
          ref={newRuleButtonRef}
          variant="secondary"
          disabled={writing}
          onClick={onNewRule}
          className="h-[26px] gap-1.5 rounded-md border border-border pr-2.5 pl-2 text-xs font-medium hover:border-foreground/20"
        >
          <RiAddLine aria-hidden="true" className="size-3.5" />
          Новое правило
        </Button>
        {status === "ready" ? (
          <span className="ml-auto text-xs font-medium tabular-nums">
            {ruleCountLabel(rules.length)}
          </span>
        ) : null}
      </div>

      {banner}

      {notice ? (
        <p role="status" className={cn("px-4 py-2 text-xs", MUTED)}>
          {notice}
        </p>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto">
        {status === "loading" ? (
          <LoadingRegion label="Загружаем правила…">
            <ul className="flex flex-col divide-y divide-border">
              {Array.from({ length: 3 }, (_, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: статичные декоративные строки
                <li key={index} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-4 w-1/3" />
                  </div>
                  <Skeleton className="h-8 w-24" />
                </li>
              ))}
            </ul>
          </LoadingRegion>
        ) : null}

        {status === "unavailable" ? (
          <div className="flex flex-col items-start gap-2 px-4 py-3.5">
            <p className={cn("text-sm", MUTED)}>Правила не прочитались: демон недоступен.</p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Повторить
            </Button>
          </div>
        ) : null}

        {status === "ready" ? (
          <>
            <TableHeadRow />
            {rules.length === 0 ? (
              <>
                <ul aria-label="Пример правила">
                  <ExampleRow />
                </ul>
                <p className={cn("px-4 py-3.5 text-xs", MUTED)}>
                  Правил нет. Правило — это событие, фильтр и действия, как в примере выше.
                </p>
              </>
            ) : (
              <ul aria-label="Правила алертов">
                {rules.map((rule) =>
                  confirmingId === rule.id ? (
                    <li key={rule.id} className="bg-destructive/5">
                      <RuleDeleteConfirm
                        rule={rule}
                        deleting={deleting}
                        writing={writing}
                        errorText={deleteError ? deleteErrorText(deleteError) : null}
                        keepButtonRef={(el) => {
                          keepButtonRef.current = el;
                        }}
                        confirmButtonRef={(el) => {
                          confirmDeleteButtonRef.current = el;
                        }}
                        onCancel={onCancelDelete}
                        onConfirm={() => onConfirmDelete(rule.id)}
                      />
                    </li>
                  ) : (
                    <RuleRow
                      key={rule.id}
                      rule={rule}
                      telegramConfigured={telegramConfigured}
                      writing={writing}
                      deleteButtonRef={registerDeleteButtonRef(rule.id)}
                      onEdit={() => onEdit(rule)}
                      onAskDelete={() => onAskDelete(rule.id)}
                      onToggleAction={(action) => onToggleAction(rule, action)}
                    />
                  ),
                )}
              </ul>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Строка статуса экрана «Алерты» (§116, design.md Д5): счётчик правил, легенда
 * значков действий и срок действия правила. Часть экрана, не UIKit.
 */
export function RuleStatusBar({ count }: { count: number | null }) {
  return (
    <div className="flex min-h-6 shrink-0 flex-wrap items-center gap-x-3 border-t border-border px-4 text-[11px] text-muted-foreground">
      {count !== null ? (
        <span className="font-medium text-foreground tabular-nums">{ruleCountLabel(count)}</span>
      ) : null}
      <span className="flex items-center gap-2">
        {ACTIONS.map((action) => (
          <span key={action.key} className="flex items-center gap-1">
            <action.Icon aria-hidden="true" className="size-3" />
            {action.label.toLowerCase()}
          </span>
        ))}
      </span>
      <span className="ml-auto">
        Правило действует со следующего события. Звук и нотификация работают, пока открыта вкладка.
      </span>
    </div>
  );
}
